import React from "react";
import { useEvent } from "../hooks/useEvent";
import { dispatchChangeEvent } from "../utils/dispatchChangeEvent";

type StepperStateProps = {
  min: number;
  max: number;
  value: number;
  step: number;
  ref: React.RefObject<HTMLInputElement>;
};

type StepperEventHandlers = {
  onClickDecrementButton: React.MouseEventHandler<HTMLButtonElement>;
  onClickIncrementButton: React.MouseEventHandler<HTMLButtonElement>;
  onMouseDownDecrementButton: React.MouseEventHandler<HTMLButtonElement>;
  onMouseDownIncrementButton: React.MouseEventHandler<HTMLButtonElement>;
  onStop(): void;
};

const useFastStep = (currentValue: number, min: number, max: number, step: number, onChange: (result: number) => void) => {
  type PassiveState = { active: false; diff?: undefined };
  type ActiveState = { active: true; diff: number };
  type State = PassiveState | ActiveState;

  const [state, setState] = React.useState<State>({ active: false });

  React.useEffect(() => {
    if (state.active === false) {
      return;
    }

    /**
     * We only care about the current value when at the state of the iteration, so we do not need to
     * include it in the list of dependencies below.
     */
    let value = currentValue;

    const callback = () => {
      value += state.diff;

      const shouldStopIncrementing = state.diff > 0 && value > max;
      const shouldStopDecrementing = state.diff < 0 && value < min;

      if (shouldStopIncrementing) {
        onChange(max);
        stop();
      } else if (shouldStopDecrementing) {
        onChange(min);
        stop();
      } else {
        onChange(value);
      }
    };

    let intervalId: NodeJS.Timeout;

    const timeoutId = setTimeout(() => {
      intervalId = setInterval(callback, 100);
    }, 200);

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [state.active]);

  const startIncrement = React.useCallback(() => {
    setState({ active: true, diff: step });
  }, []);

  const startDecrement = React.useCallback(() => {
    setState({ active: true, diff: -step });
  }, []);

  const stop = React.useCallback(() => {
    setState({ active: false });
  }, []);

  return { startIncrement, startDecrement, stop };
};

const calculateNextDecrement = (value: number, min: number, step: number): number => {
  const diff = value - min;
  const remainder = diff % step;
  if (remainder === 0) {
    return Math.max(value - step, min);
  } else {
    return value - remainder;
  }
};

const calculateNextIncrement = (value: number, min: number, max: number, step: number): number => {
  const diff = value - min;
  const remainder = diff % step;

  if (remainder === 0) {
    return Math.min(value + step, max);
  } else {
    return value + (step - remainder);
  }
};

export const useStepperEventHandlers = (props: StepperStateProps): StepperEventHandlers => {
  const { min, max, value, ref, step } = props;

  const triggerOnChange = useEvent((result: number) => {
    const input = ref.current;
    if (!input) return;

    dispatchChangeEvent(input, result);
  });

  const fastStep = useFastStep(value, min, max, step, triggerOnChange);

  const onClickDecrementButton = useEvent<React.MouseEventHandler<HTMLButtonElement>>((ev) => {
    ev.preventDefault();

    if (value > min) {
      const next = calculateNextDecrement(value, min, step);
      triggerOnChange(next);
    }
  });

  const onClickIncrementButton = useEvent<React.MouseEventHandler<HTMLButtonElement>>((ev) => {
    ev.preventDefault();

    if (value < max) {
      const next = calculateNextIncrement(value, min, max, step);
      triggerOnChange(next);
    }
  });

  const onMouseDownDecrementButton = useEvent<React.MouseEventHandler<HTMLButtonElement>>((ev) => {
    ev.preventDefault();
    ev.currentTarget.focus();
    fastStep.startDecrement();
  });

  const onMouseDownIncrementButton = useEvent<React.MouseEventHandler<HTMLButtonElement>>((ev) => {
    ev.preventDefault();
    ev.currentTarget.focus();
    fastStep.startIncrement();
  });

  const onStop = useEvent(() => {
    fastStep.stop();
  });

  return React.useMemo(() => {
    return {
      onClickDecrementButton,
      onClickIncrementButton,
      onMouseDownDecrementButton,
      onMouseDownIncrementButton,
      onStop,
    };
  }, []);
};
